import React, { useCallback, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { camelToSnake } from '../../shared/functions'

const MANAGED_BOOKMARK_GROUPS_FIELD_PREFIX = 'safari_bookmark[managed_bookmark_groups_attributes]'
const DEFAULT_MAX_BOOKMARKS_DEPTH = 10

function formatErrorMessages(messages) {
  return messages.join('. ')
}

function normalizeBookmarkTree(bookmarks) {
  return (bookmarks || []).map((bookmark) => {
    const fieldErrors = bookmark.errors || {}
    return {
      title: bookmark.title || '',
      url: bookmark.url || '',
      bookmarkType: bookmark.bookmarkType || '',
      folder: normalizeBookmarkTree(bookmark.folder),
      ...(Object.keys(fieldErrors).length > 0 ? { errors: fieldErrors } : {})
    }
  })
}

function buildGroupRowErrorsForList(groupErrors) {
  if (!groupErrors) {
    return undefined
  }
  const rowErrors = {}
  if (groupErrors.groupIdentifier) {
    rowErrors.groupIdentifier = groupErrors.groupIdentifier
  }
  if (groupErrors.title) {
    rowErrors.title = groupErrors.title
  }
  return Object.keys(rowErrors).length ? rowErrors : undefined
}

function managedBookmarkGroupColumnName(rowIndex, columnKey) {
  return `${MANAGED_BOOKMARK_GROUPS_FIELD_PREFIX}[${rowIndex}][${camelToSnake(columnKey)}]`
}

function normalizeManagedBookmarkGroups(groups) {
  return (groups || []).map((group) => ({
    groupIdentifier: group.groupIdentifier || '',
    title: group.title || '',
    bookmarks: normalizeBookmarkTree(group.bookmarks),
    errors: buildGroupRowErrorsForList(group.errors),
    ...(group.id != null ? { id: group.id } : {})
  }))
}

function buildPrimaryHeaders(tooltips) {
  return [
    {
      displayName: 'Group Identifier',
      key: 'groupIdentifier',
      tooltip: tooltips.groupIdentifier
    },
    {
      displayName: 'Folder Title',
      key: 'title',
      tooltip: tooltips.title
    }
  ]
}

function buildBookmarksSectionHeader(tooltips) {
  return {
    displayName: 'Bookmarks',
    key: 'bookmarks',
    tooltip: tooltips.bookmarks
  }
}

function headerLabelContent(header) {
  if (header.tooltip) {
    return (
      <>
        {header.displayName}
        &nbsp;
        <OverlayTrigger
          placement="bottom"
          overlay={<Tooltip id={`${header.key}-tooltip`}>{header.tooltip}</Tooltip>}>
          <span className="glyphicon glyphicon-question-sign" />
        </OverlayTrigger>
      </>
    )
  }
  return header.displayName
}

function BookmarkTree({ entries, pathPrefix, policy, onChange, depth, maxDepth }) {
  const bookmarkEntries = entries || []

  const replaceEntryAtIndex = (index, updatedEntry) => {
    const nextEntries = [...bookmarkEntries]
    nextEntries[index] = updatedEntry
    onChange(nextEntries)
  }

  const removeEntryAtIndex = (index) => {
    onChange(bookmarkEntries.filter((_entry, entryIndex) => entryIndex !== index))
  }

  const appendEmptyBookmark = () => {
    onChange([...bookmarkEntries, { title: '', url: '', folder: [], bookmarkType: '' }])
  }

  return (
    <div className="bookmarks-item-tree">
      {bookmarkEntries.map((entry, index) => (
        <BookmarksItemEditor
          key={index}
          entry={entry || {}}
          pathPrefix={`${pathPrefix}[${index}]`}
          policy={policy}
          depth={depth}
          maxDepth={maxDepth}
          onChange={(nextEntry) => replaceEntryAtIndex(index, nextEntry)}
          onRemove={() => removeEntryAtIndex(index)}
        />
      ))}
      {policy.update && (
        <button type="button" className="btn btn-default btn-xs bookmarks-item-tree-add" onClick={appendEmptyBookmark}>
          Add bookmark
        </button>
      )}
    </div>
  )
}

function BookmarksItemEditor({ entry, pathPrefix, policy, onChange, onRemove, depth, maxDepth }) {
  const title = entry.title || ''
  const url = entry.url || ''
  const nestedBookmarks = entry.folder || []
  const bookmarkType = entry.bookmarkType || 'url'
  const fieldErrors = entry.errors || {}
  const canBeFolder = depth <= maxDepth || bookmarkType === 'folder'

  const setBookmarkType = (bookmarkType) => {
    if (bookmarkType === 'url') {
      onChange({ ...entry, url: url, folder: [], bookmarkType: 'url' })
    } else {
      onChange({ ...entry, url: '', folder: nestedBookmarks, bookmarkType: 'folder' })
    }
  }

  const setTitle = (value) => {
    onChange({ ...entry, title: value })
  }

  const setUrl = (value) => {
    onChange({ ...entry, url: value, folder: [], bookmarkType: 'url' })
  }

  const setNestedBookmarks = (nextNestedBookmarks) => {
    onChange({ ...entry, url: '', folder: nextNestedBookmarks, bookmarkType: 'folder' })
  }

  return (
    <div className="bookmarks-item-entry well well-sm">
      <div className="row">
        <div className={`col-sm-4${fieldErrors.title?.length ? ' has-error' : ''}`}>
          <label className="control-label">Title</label>
          <input
            type="text"
            className="form-control"
            name={`${pathPrefix}[title]`}
            value={title}
            disabled={!policy.update}
            onChange={(event) => setTitle(event.target.value)}
          />
          {fieldErrors.title?.length > 0 && (
            <span className="help-block">{formatErrorMessages(fieldErrors.title)}</span>
          )}
        </div>
        <div className="col-sm-6">
          <label className="control-label">Type</label>
          <div className="bookmarks-item-entry-type">
            {canBeFolder && (
              <label className="radio-inline">
                <input
                  type="radio"
                  name={`${pathPrefix}[bookmark_type]`}
                  value="folder"
                  checked={bookmarkType === 'folder'}
                  disabled={!policy.update}
                  onChange={() => setBookmarkType('folder')}
                />
                {' '}
                Folder
              </label>
            )}
            <label className="radio-inline">
              <input
                type="radio"
                name={`${pathPrefix}[bookmark_type]`}
                value="url"
                checked={bookmarkType === 'url'}
                disabled={!policy.update}
                onChange={() => setBookmarkType('url')}
              />
              {' '}
              URL
            </label>
          </div>
        </div>
        <div className="col-sm-2">
          {policy.update && (
            <>
              <label className="control-label">&nbsp;</label>
              <button type="button" className="btn btn-default btn-block" onClick={onRemove}>
                Remove
              </button>
            </>
          )}
        </div>
      </div>
      {bookmarkType === 'url' && (
        <div className="row">
          <div className={`col-sm-10${fieldErrors.url?.length ? ' has-error' : ''}`}>
            <label className="control-label">URL</label>
            <input
              type="text"
              className="form-control"
              name={`${pathPrefix}[url]`}
              value={url}
              disabled={!policy.update}
              onChange={(event) => setUrl(event.target.value)}
            />
            {fieldErrors.url?.length > 0 && (
              <span className="help-block">{formatErrorMessages(fieldErrors.url)}</span>
            )}
          </div>
        </div>
      )}
      {bookmarkType === 'url' && fieldErrors.folder?.length > 0 && (
        <div className="row">
          <div className="col-sm-10">
            <span className="help-block"><span className="text-danger">{formatErrorMessages(fieldErrors.folder)}</span></span>
          </div>
        </div>
      )}
      {bookmarkType === 'folder' && (
        <div className="bookmarks-item-folder-children">
          <label className="control-label">Nested bookmarks</label>
          {fieldErrors.folder?.length > 0 && (
            <span className="help-block"><span className="text-danger">{formatErrorMessages(fieldErrors.folder)}</span></span>
          )}
          <BookmarkTree
            entries={nestedBookmarks}
            pathPrefix={`${pathPrefix}[folder_attributes]`}
            policy={policy}
            depth={depth + 1}
            maxDepth={maxDepth}
            onChange={setNestedBookmarks}
          />
        </div>
      )}
    </div>
  )
}

function ManagedBookmarkGroups(props) {
  const { data, policy, tooltips = {}, maxBookmarksDepth = DEFAULT_MAX_BOOKMARKS_DEPTH } = props
  const primaryHeaders = buildPrimaryHeaders(tooltips)
  const bookmarksSectionHeader = buildBookmarksSectionHeader(tooltips)
  const [state, setState] = useState({
    items: normalizeManagedBookmarkGroups(data.managedBookmarkGroups)
  })

  const onAddItem = useCallback(() => {
    setState((previousState) => ({
      items: [
        ...previousState.items,
        {
          groupIdentifier: '',
          title: '',
          bookmarks: []
        }
      ]
    }))
  }, [])

  const onRemoveItem = useCallback((_row, rowIndex) => {
    setState((previousState) => ({
      items: previousState.items.filter((_item, index) => index !== rowIndex)
    }))
  }, [])

  const onChangeItemField = useCallback((rowIndex, fieldKey, value) => {
    setState((previousState) => ({
      items: previousState.items.map((item, index) => {
        return index === rowIndex ? { ...item, [fieldKey]: value } : item
      })
    }))
  }, [])

  const onChangeBookmarks = useCallback((rowIndex, nextBookmarks) => {
    setState((previousState) => ({
      items: previousState.items.map((item, index) => {
        return index === rowIndex ? { ...item, bookmarks: nextBookmarks } : item
      })
    }))
  }, [])

  const columnCount = primaryHeaders.length + (policy.update ? 1 : 0)

  return (
    <div className="managed-bookmark-groups dynamic-list">
      {state.items.length === 0 ? (
        <input type="hidden" name={`${MANAGED_BOOKMARK_GROUPS_FIELD_PREFIX}[]`} value="" />
      ) : null}
      {state.items.map((row, rowIndex) => (
        <table
          key={`managed-bookmark-group-${rowIndex}`}
          className="table table-condensed managed-bookmark-group-table">
          <thead>
            <tr>
              {primaryHeaders.map((header) => (
                <th key={header.key}>{headerLabelContent(header)}</th>
              ))}
              {policy.update && <th key="actions" className="actions" />}
            </tr>
          </thead>
          <tbody>
            <tr>
              {primaryHeaders.map((header, headerIndex) => {
                const fieldKey = header.key
                const columnName = managedBookmarkGroupColumnName(rowIndex, fieldKey)
                const inputDisabled = !policy.update
                const fieldMessages =
                  row.errors && Array.isArray(row.errors[fieldKey]) && row.errors[fieldKey].length > 0
                    ? row.errors[fieldKey]
                    : null
                return (
                  <td key={fieldKey}>
                    {headerIndex === 0 && row.id != null ? (
                      <input type="hidden" name={managedBookmarkGroupColumnName(rowIndex, 'id')} value={row.id} />
                    ) : null}
                    <div className={fieldMessages ? 'has-error' : ''}>
                      <input
                        type="text"
                        className="form-control"
                        value={row[fieldKey]}
                        name={columnName}
                        id={columnName}
                        disabled={inputDisabled}
                        onChange={(event) => onChangeItemField(rowIndex, fieldKey, event.target.value)}
                      />
                      {fieldMessages ? <span className="help-block">{fieldMessages.join('. ')}</span> : null}
                    </div>
                  </td>
                )
              })}
              {policy.update && (
                <td key="remove">
                  <a
                    className="delete-button"
                    href="#delete"
                    onClick={(event) => {
                      event.preventDefault()
                      onRemoveItem(row, rowIndex)
                    }}>
                    <span className="glyphicon glyphicon-remove-circle" />
                  </a>
                </td>
              )}
            </tr>
            <tr>
              <td colSpan={columnCount}>
                <div className="managed-bookmark-group-table-full-width-heading">{headerLabelContent(bookmarksSectionHeader)}</div>
                <BookmarkTree
                  entries={row.bookmarks}
                  pathPrefix={`${MANAGED_BOOKMARK_GROUPS_FIELD_PREFIX}[${rowIndex}][bookmarks_attributes]`}
                  policy={policy}
                  depth={1}
                  maxDepth={maxBookmarksDepth}
                  onChange={(nextBookmarks) => onChangeBookmarks(rowIndex, nextBookmarks)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      ))}
      {policy.update && (
        <a
          className="add-button"
          href="#add"
          title="Add managed bookmark group"
          onClick={(event) => {
            event.preventDefault()
            onAddItem()
          }}>
          <span className="glyphicon glyphicon-plus-sign" />
        </a>
      )}
    </div>
  )
}

export default ManagedBookmarkGroups
